import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Session } from "@supabase/supabase-js";

interface User {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  email_confirmed_at?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Utility function to extract project ID from Supabase URL
 * @returns Project ID or null if URL is invalid or missing
 */
const getSupabaseProjectId = (): string | null => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) return null;
  const match = url.match(/https?:\/\/([^.]+)\.supabase\.co/);
  return match ? match[1] : null;
};

/**
 * Clean up all Supabase localStorage keys for the current project
 * This ensures proper cleanup when logging out or migrating to a new project
 */
const cleanupSupabaseStorage = () => {
  if (typeof window === "undefined") return;

  const projectId = getSupabaseProjectId();
  if (!projectId) {
    // Fallback: clear all sb-* keys if project ID extraction fails
    Object.keys(localStorage)
      .filter((key) => key.startsWith("sb-"))
      .forEach((key) => localStorage.removeItem(key));
    return;
  }

  // Clear all keys for this specific project
  Object.keys(localStorage)
    .filter((key) => key.startsWith(`sb-${projectId}-`))
    .forEach((key) => localStorage.removeItem(key));
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let hasSetLoading = false;

    // Check if supabase client is available
    if (!supabase) {
      console.warn(
        "Supabase client not available, skipping auth initialization",
      );
      setIsLoading(false);
      return;
    }

    // Set a maximum loading time to prevent indefinite loading
    const maxLoadingTimeout = setTimeout(() => {
      if (!hasSetLoading && isMounted) {
        console.log("Max loading time reached, setting loading to false");
        setIsLoading(false);
        hasSetLoading = true;
      }
    }, 1000); // 1 second max loading time

    // Get initial session first
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;

      console.log(
        "Initial session check:",
        session ? "Session found" : "No session",
      );
      setSession(session);
      if (session?.user) {
        console.log("Setting user from initial session:", session.user.email);
        setUser({
          id: session.user.id,
          email: session.user.email || "",
          name:
            session.user.user_metadata.full_name || session.user.email || "",
          avatar_url: session.user.user_metadata.avatar_url,
          email_confirmed_at: session.user.email_confirmed_at,
        });
      } else {
        console.log("No user in initial session");
        setUser(null);
      }

      if (!hasSetLoading) {
        setIsLoading(false);
        hasSetLoading = true;
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      console.log(
        "Auth state change:",
        event,
        session ? "Session active" : "No session",
      );
      setSession(session);
      if (session?.user) {
        console.log("Setting user from auth change:", session.user.email);
        setUser({
          id: session.user.id,
          email: session.user.email || "",
          name:
            session.user.user_metadata.full_name || session.user.email || "",
          avatar_url: session.user.user_metadata.avatar_url,
          email_confirmed_at: session.user.email_confirmed_at,
        });
      } else {
        console.log("Clearing user from auth change");
        setUser(null);
      }

      if (!hasSetLoading) {
        setIsLoading(false);
        hasSetLoading = true;
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(maxLoadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    if (!supabase) {
      throw new Error("Supabase client not available");
    }
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    if (!supabase) {
      throw new Error("Supabase client not available");
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/chat`,
        skipBrowserRedirect: false, // Use popup
      },
    });
    if (error) throw error;
  };

  const logout = async () => {
    if (!supabase) {
      console.warn("Supabase client not available, clearing local state");
      setUser(null);
      setSession(null);
      return;
    }

    try {
      console.log("Logging out user...");

      // Try to sign out with local scope first (recommended)
      const { error } = await supabase.auth.signOut({ scope: "local" });

      if (error) {
        console.warn("Local logout failed, trying without scope:", error);
        // Fallback: try without scope
        const { error: fallbackError } = await supabase.auth.signOut();
        if (fallbackError) {
          console.warn(
            "Supabase logout failed, clearing local state manually:",
            fallbackError,
          );
          // Clear local state manually if Supabase logout fails
          setUser(null);
          setSession(null);
          // Also try to clear any stored session data
          if (typeof window !== "undefined") {
            cleanupSupabaseStorage();
            sessionStorage.clear();
          }
          return; // Don't throw error, just clear state
        }
      }

      console.log("Logout successful");
      // Note: onAuthStateChange will handle clearing user/session state
    } catch (error) {
      console.error("Logout failed:", error);
      // Clear local state even if logout fails
      setUser(null);
      setSession(null);
      // Also try to clear any stored session data
      if (typeof window !== "undefined") {
        cleanupSupabaseStorage();
        sessionStorage.clear();
      }
      // Don't throw error, just clear state
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, session, login, loginWithGoogle, logout, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
